// thunks.ts
import type { ThunkAction } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
import type { UnknownAction } from 'redux';

import type { Company } from '../../@types/Company';
import type { Fonction } from '../../@types/Fonction';
import type { User } from '../../@types/User';
import {
  resetAvailableCompanies, resetSelectedCompany, resetSelectedFonction, resetToken, resetUser, setAuthReady, setAvailableCompanies,
  setSelectedCompany, setSelectedFonction, setToken, setUser,
} from '../../store/AuthReducer';
import type { AppDispatch, RootState } from '../../store/store';
import { JWTHeader } from '../../utils/JWTHeader';

interface ProxyTokenUseResponse {
  jwt: string;
}

const getCookieExpiration = (): number => {
  const ttl = process.env.REACT_APP_JWT_TOKEN_TTL;
  if (ttl) {
    const ttlNumber = parseInt(ttl, 10);
    return isNaN(ttlNumber) ? 1 : ttlNumber / (24 * 60 * 60);
  }
  return 1; // Default to 1 day if REACT_APP_JWT_TOKEN_TTL is not set
};

const getCompanyFromDJ = async(jwt: string, companyId: number | string): Promise<Company> => {
  const response = await fetch(`/api/companies/${companyId}`, JWTHeader(jwt));
  const data = await response.json();
  return data;
};

type ValidateCompanyResult = { company: Company; fonction?: Fonction } | false;

const validateCompanyId = async(userData: User, companyFromDJ: number | string, jwt: string): Promise<ValidateCompanyResult> => {
  if ((userData?.roles ?? []).includes('ROLE_ADMIN')) {
    const adminCompany = await getCompanyFromDJ(jwt, companyFromDJ);
    return { company: adminCompany };
  }
  if (userData && userData.fonctions && userData.fonctions.length > 0) {
    const fonctionSelected = userData.fonctions.find((fonction: Fonction) => fonction.company.id == companyFromDJ);
    if (fonctionSelected) {
      const fetchedCompany = await getCompanyFromDJ(jwt, companyFromDJ);
      return { company: fetchedCompany, fonction: fonctionSelected };
    }
    return false;
  }
  return false;
};

const createAdminFonction = (company: Company): Fonction => ({
  id: 0,
  type: 0,
  company,
});

export const logout = (dispatch: AppDispatch): void => {
  dispatch(resetToken());
  dispatch(resetUser());
  dispatch(resetSelectedCompany());
  dispatch(resetSelectedFonction());
  dispatch(resetAvailableCompanies());
};

const loginUsingProxyToken = async(dispatch: AppDispatch, proxyToken: string, companyFromDJ: number | string): Promise<boolean | undefined> => {
  const response = await fetch(`/api/public/proxy-tokens/${proxyToken}/use`, { method: 'PATCH' });
  const data = await response.json() as ProxyTokenUseResponse;
  if (data.jwt) {
    const expirationInDays = getCookieExpiration();
    Cookies.set('jwt', data.jwt, { expires: expirationInDays });
    Cookies.set('companySelected', companyFromDJ.toString(), { expires: expirationInDays });
    const responseUserMe = await fetch('/api/users/me', JWTHeader(data.jwt));
    if (responseUserMe.status === 200) {
      const userData = await responseUserMe.json() as User;
      const fonctionWithSelectedCompany = await validateCompanyId(userData, companyFromDJ, data.jwt);
      if (fonctionWithSelectedCompany) {
        dispatch(setToken(data.jwt));
        dispatch(setUser(userData));
        const fonction = fonctionWithSelectedCompany.fonction ?? createAdminFonction(fonctionWithSelectedCompany.company);
        dispatch(setSelectedFonction(fonction));
        dispatch(setSelectedCompany(fonctionWithSelectedCompany.company));

        const availableCompanies = userData.fonctions.map((f) => f.company);
        dispatch(setAvailableCompanies(availableCompanies));

        return true;
      }
      logout(dispatch);
    } else {
      logout(dispatch);
    }
  }
};

const loginUsingJWT = async(dispatch: AppDispatch, jwt: string, companySelectedId: number, currentCompany: Company | null): Promise<boolean | undefined> => {
  const response = await fetch('/api/users/me', {
    headers: {
      Authorization: `Bearer ${jwt}`,
    },
  });
  if (response.status === 200) {
    const userData = await response.json() as User;
    let fonctionWithSelectedCompany: Fonction | { company: Company } | undefined = userData.fonctions.find(
      (fonction: Fonction) => fonction.company.id == companySelectedId,
    );
    if ((userData?.roles ?? []).includes('ROLE_ADMIN')) {
      const company = await getCompanyFromDJ(jwt, companySelectedId);
      fonctionWithSelectedCompany = { company };
    }
    if (fonctionWithSelectedCompany) {
      const expirationInDays = getCookieExpiration();
      Cookies.set('jwt', jwt, { expires: expirationInDays });
      Cookies.set('companySelected', companySelectedId.toString(), { expires: expirationInDays });
      dispatch(setToken(jwt));
      dispatch(setUser(userData));
      const fonction = 'id' in fonctionWithSelectedCompany
        ? fonctionWithSelectedCompany
        : createAdminFonction(fonctionWithSelectedCompany.company);
      dispatch(setSelectedFonction(fonction));
      if (!currentCompany || currentCompany.id !== fonctionWithSelectedCompany.company.id) {
        dispatch(setSelectedCompany(fonctionWithSelectedCompany.company));
      }

      const availableCompanies = userData.fonctions.map((f) => f.company);
      dispatch(setAvailableCompanies(availableCompanies));

      return true;
    }
    logout(dispatch);
  } else {
    logout(dispatch);
  }
};

export const checkJwt = (
  accessToken: string | null,
  companyFromDJ: number | null,
  currentCompany: Company | null,
): ThunkAction<Promise<boolean>, RootState, unknown, UnknownAction> => async(dispatch: AppDispatch): Promise<boolean> => {
  let isLogged = false;

  if (accessToken && companyFromDJ) {
    try {
      isLogged = await loginUsingProxyToken(dispatch, accessToken, companyFromDJ) || false;
    } catch (error) {
      console.error('Error :', (error as Error).message);
    }
  } else {
    const jwt = Cookies.get('jwt');
    const companySelectedId = Cookies.get('companySelected');

    if (jwt && companySelectedId) {
      try {
        isLogged = await loginUsingJWT(dispatch, jwt, parseInt(companySelectedId, 10), currentCompany) || false;
      } catch (error) {
        console.error('Error :', (error as Error).message);
      }
    }
  }

  dispatch(setAuthReady(true));
  return isLogged;
};
